import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/auth.store';
import { useModalStore } from '../../stores/modal.store';
import { Icon } from '../../components/ui/Icon';
import { Chip } from '../../components/ui/Chip';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { Pagination } from '../../components/ui/Pagination';
import { MobileFilters } from '../../components/ui/MobileFilters';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { useUrlState } from '../../hooks/useUrlState';
import { fmtEur, fmtDate } from '../../lib/utils';
import { usciteQuery, daysFromToday, plurale, METHOD_LABEL, SOURCE_LABEL, SOURCE_TO_MATCH, isSuperAdmin, type UsciteFilter, type UscitaRow } from './adminApi';
import { useAdminActions } from './useAdminActions';
import { SettlePopover } from './SettlePopover';

/**
 * Uscite — tabella unificata: fornitori, ricorrenti, tasse, contanti (fonte come badge).
 *   filterbar → pill Aperte / Pagate / Tutte · cerca · contatore · Nuova uscita/ricorrente
 *   tabella   → Fonte · Controparte/Descrizione · Scadenza · Importo · Stato · Paga
 * Le righe PERSONALE_ANDREA le vede solo il Super Admin (badge "Personale").
 * Stato in URL: status · q
 */
const FILTERS: Array<{ key: UsciteFilter; label: string }> = [
  { key: 'aperte', label: 'Aperte' },
  { key: 'pagate', label: 'Pagate' },
  { key: 'tutte', label: 'Tutte' },
];
const PAGE_SIZE = 25;

export function UsciteTab({ canWrite, showHistory }: { canWrite: boolean; showHistory?: boolean }) {
  const user = useAuthStore((s) => s.user);
  const superAdmin = isSuperAdmin(user);
  const openModal = useModalStore((s) => s.open);
  const [statusParam, setStatus] = useUrlState<string>('status', 'aperte');
  const status: UsciteFilter = FILTERS.some((f) => f.key === statusParam) ? (statusParam as UsciteFilter) : 'aperte';
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [status, search, showHistory]);

  const params = useMemo(
    () => ({ status, search: search.trim() || undefined, includeHistory: showHistory, page, pageSize: PAGE_SIZE }),
    [status, search, showHistory, page],
  );
  const q = useQuery(usciteQuery(params));
  // Difensivo: l'API dovrebbe già filtrare per ruolo, ma le righe personali non passano comunque
  const items = (q.data?.items ?? []).filter((r) => superAdmin || r.entity !== 'PERSONALE_ANDREA');

  return (
    <>
      {/* Sul telefono i controlli erano quattro righe impilate alte 210px, che
          spingevano la lista sotto la piega: dietro un tasto solo (mobile.md §5).
          Sul computer MobileFilters non mette nemmeno un <div>: identico. */}
      <MobileFilters attivi={[search.trim(), status !== 'aperte' ? status : ''].filter(Boolean).length}>
      <div className="filterbar filterbar--float mb-4 flex-wrap">
        <div className="flex items-center gap-1.5" role="group" aria-label="Stato uscite">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => setStatus(f.key)} aria-pressed={status === f.key} className={status === f.key ? 'filter-pill is-active' : 'filter-pill'}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="divider" />
        <div className="tb-search flex-1" style={{ minWidth: 200, margin: 0 }}>
          <Icon name="search" size={12} />
          <input aria-label="Cerca uscite" placeholder="Cerca fornitore o descrizione…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <span className="mute text-[12px]">{plurale(q.data?.total ?? 0, 'uscita', 'uscite')}</span>
        {/* I due bottoni non sono filtri: sul telefono non vanno nel pannello
            dei filtri ma in fondo alla schermata (mobile.md §4). */}
        {canWrite && (
          <>
            <button className="btn btn-sm d-only" onClick={() => openModal('newCashEntry', { direction: 'OUT' })}>
              <Icon name="banknote" size={10} /> Nuova uscita in contanti
            </button>
            <button className="btn btn-sm d-only" onClick={() => openModal('newRecurring')}>
              <Icon name="repeat" size={10} /> Nuova spesa ricorrente
            </button>
          </>
        )}
      </div>
      </MobileFilters>

      {q.isLoading && !q.data ? (
        <div className="ed-section"><table className="tbl tbl--ed"><tbody><SkeletonTable rows={6} cols={6} withAvatar={false} /></tbody></table></div>
      ) : items.length === 0 ? (
        status === 'aperte' && !search.trim()
          ? <EmptyState brand title="Niente da pagare" description="Fatture ricevute, ricorrenti e tasse aperte compaiono qui." />
          : <EmptyState brand title="Nessuna uscita" description="Cambia filtro o azzera la ricerca." />
      ) : (
        <div className="ed-section">
          {/* Scheda sul telefono; la riga non apre un dettaglio (il gesto è
              "Paga", dentro la scheda), quindi niente chevron: `tbl--static`. */}
          <table className="tbl tbl--ed tbl--card tbl--static">
            <thead>
              <tr>
                <th style={{ width: 110 }}>Fonte</th>
                <th>Controparte</th>
                <th style={{ width: 170 }}>Scadenza</th>
                <th className="num" style={{ width: 130 }}>Importo</th>
                <th style={{ width: 150 }}>Stato</th>
                <th style={{ width: 100 }} />
              </tr>
            </thead>
            <tbody>
              {items.map((r) => <Row key={`${r.source}-${r.id}`} r={r} canWrite={canWrite} />)}
            </tbody>
          </table>
          {q.data && <Pagination page={page} pageSize={q.data.pageSize} total={q.data.total} totalPages={q.data.totalPages} onPage={setPage} />}
        </div>
      )}

      {/* Due azioni, non tre: è il massimo che regge la barra (mobile.md §4). */}
      {canWrite && (
        <DesktopOnly
          cosa="La registrazione di nuove uscite"
          perche="contanti e ricorrenze richiedono dati contabili completi; dal telefono puoi pagare le uscite già presenti."
        >
          <div className="m-actionbar m-only">
            <button className="btn btn-primary" onClick={() => openModal('newCashEntry', { direction: 'OUT' })}>
              <Icon name="banknote" size={12} /> Contanti
            </button>
            <button className="btn" onClick={() => openModal('newRecurring')}>
              <Icon name="repeat" size={12} /> Ricorrente
            </button>
          </div>
        </DesktopOnly>
      )}
    </>
  );
}

function Row({ r, canWrite }: { r: UscitaRow; canWrite: boolean }) {
  const { pay } = useAdminActions();
  const [open, setOpen] = useState(false);
  const paid = ['PAGATA', 'PAGATO', 'PAID'].includes(String(r.status).toUpperCase());
  const matchKind = SOURCE_TO_MATCH[r.source];
  const days = daysFromToday(r.dueDate);
  const overdue = !paid && days != null && days < 0;
  const dueLabel = paid ? fmtDate(r.dueDate)
    : days == null ? '—'
    : days < 0 ? `in ritardo di ${-days} g`
    : days === 0 ? 'oggi'
    : days === 1 ? 'domani'
    : days <= 6 ? `tra ${days} g`
    : fmtDate(r.dueDate);
  return (
    <tr style={{ cursor: 'default' }}>
      {/* La fonte è già scritta nel sottotitolo della scheda: sul telefono una
          cella in più per "Tasse" toglie spazio a quello che si cerca. */}
      <td data-m="off"><span className="t-micro">{SOURCE_LABEL[r.source] ?? r.source}</span></td>
      <td data-m="hd" className="adm-clip">
        <div className="font-semibold flex items-center gap-2">
          <span className="truncate">{r.counterparty || '—'}</span>
          {r.entity === 'PERSONALE_ANDREA' && <Chip tone="ghost">Personale</Chip>}
        </div>
        {/* La colonna "Fonte" sul telefono sparisce: l'etichetta si rifà viva
            qui sotto, dove il dato serve a capire di che uscita si tratta.
            `.m-only` è spento sopra i 768px, quindi il computer non cambia. */}
        <div className="text-[12px] mute m-only">{SOURCE_LABEL[r.source] ?? r.source}</div>
        {r.description && <div className="text-[12px] mute" title={r.description}>{r.description}</div>}
      </td>
      <td data-m="key" data-l="Scadenza" className="mono text-[13px]" style={{ whiteSpace: 'nowrap' }}>
        <span className={overdue ? 'text-alert font-semibold' : days != null && days <= 1 && !paid ? 'text-warn' : 'mute'}>{dueLabel}</span>
      </td>
      <td data-m="key" data-l="Importo" className="num mono font-semibold" style={{ whiteSpace: 'nowrap' }}>€ {fmtEur(r.amount, true)}</td>
      <td data-m="key" data-l="Stato">
        {paid
          ? <Chip tone="ok" dot>Pagata{r.paidDate ? ` · ${fmtDate(r.paidDate)}` : ''}{r.method ? ` · ${METHOD_LABEL[r.method] ?? r.method}` : ''}</Chip>
          : overdue ? <Chip tone="alert" dot>Scaduta</Chip> : <Chip tone="warn" dot>Da pagare</Chip>}
      </td>
      {/* "Paga" è il motivo per cui questa schermata si apre dal telefono: una
          riga tutta sua in fondo alla scheda, non un'icona in un angolo. */}
      <td data-m="row" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-end gap-1 relative">
          {canWrite && !paid && (
            <button className="btn btn-sm" disabled={pay.isPending} title="Registra il pagamento" onClick={() => setOpen((v) => !v)}>
              <Icon name="check" size={10} /> Paga
            </button>
          )}
          {open && (
            <SettlePopover
              mode="paga"
              amount={r.amount}
              target={matchKind ? { kind: matchKind, id: r.id } : undefined}
              counterparty={r.counterparty}
              side="DEBIT"
              // Solo le fatture ricevute stanno su FiC: le altre uscite non creano
              // nessuna differenza da spiegare.
              withNote={r.source === 'PAYABLE'}
              pending={pay.isPending}
              onClose={() => setOpen(false)}
              onConfirm={(v) => {
                setOpen(false);
                pay.mutate({ source: r.source, id: r.id, amount: r.amount, label: r.counterparty, dueDate: r.dueDate, ...v });
              }}
            />
          )}
        </div>
      </td>
    </tr>
  );
}
